import { BlockMath, InlineMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

const options = [
  { value: "styczne zewnętrznie", label: "styczne zewnętrznie" },
  { value: "styczne wewnętrznie", label: "styczne wewnętrznie" },
  { value: "przecinają się", label: "przecinają się" },
  { value: "rozłączne zewnętrznie", label: "rozłączne zewnętrznie" },
  { value: "rozłączne wewnętrznie", label: "rozłączne wewnętrznie" },
];
const positions = options.map(({ value }) => value);
const EPSILON = 1e-6;

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function parenIfNeg(n) {
  return n < 0 ? `(${n})` : `${n}`;
}

function latexDiff(a, b) {
  // zawsze pokaż b w nawiasie, jeśli ujemne: 2 - (-3)
  return `${a} - ${parenIfNeg(b)}`;
}

export function classifyCirclePosition(d, r1, r2) {
  const rSum = r1 + r2;
  const rDiff = Math.abs(r1 - r2);
  if (d === 0 && r1 === r2) return "pokrywają się";
  if (Math.abs(d - rSum) < EPSILON) return "styczne zewnętrznie";
  if (Math.abs(d - rDiff) < EPSILON) return "styczne wewnętrznie";
  if (d > rSum) return "rozłączne zewnętrznie";
  if (d < rDiff) return "rozłączne wewnętrznie";
  return "przecinają się";
}

function randomCoordinateForOffset(offset) {
  const min = Math.max(-5, -5 - offset);
  const max = Math.min(5, 5 - offset);
  return getRandomInt(min, max);
}

function drawRadii(position) {
  const r1 = getRandomInt(2, 5);
  const allowedR2 = [2, 3, 4, 5].filter((r2) => {
    if (position === "styczne wewnętrznie") return r2 !== r1;
    if (position === "rozłączne wewnętrznie") return Math.abs(r1 - r2) >= 2;
    return true;
  });
  return { r1, r2: allowedR2[getRandomInt(0, allowedR2.length - 1)] };
}

function drawOffset(position, r1, r2) {
  const candidates = [];
  for (let dx = -10; dx <= 10; dx += 1) {
    for (let dy = -10; dy <= 10; dy += 1) {
      if (dx === 0 && dy === 0) continue;
      const distance = Math.sqrt(dx ** 2 + dy ** 2);
      if (classifyCirclePosition(distance, r1, r2) === position) {
        candidates.push({ dx, dy });
      }
    }
  }
  return candidates[getRandomInt(0, candidates.length - 1)];
}

export function generateTask(settings = {}) {
  const position = positions.includes(settings?.position)
    ? settings.position
    : positions[getRandomInt(0, positions.length - 1)];
  const { r1, r2 } = drawRadii(position);
  const { dx, dy } = drawOffset(position, r1, r2);
  const x1 = randomCoordinateForOffset(dx);
  const y1 = randomCoordinateForOffset(dy);
  const x2 = x1 + dx;
  const y2 = y1 + dy;
  const d = Math.sqrt(dx ** 2 + dy ** 2);

  return {
    type: "CircleCirclePositionTask",
    data: {
      circle1: { x: x1, y: y1, r: r1 },
      circle2: { x: x2, y: y2, r: r2 },
      d,
      position,
    },
  };
}

function formatShiftedVar(variable, shift) {
  if (shift === 0) return `${variable}^2`;
  const sign = shift > 0 ? "-" : "+";
  return `(${variable} ${sign} ${Math.abs(shift)})^2`;
}

export function renderTask(task) {
  const data = task.data ?? task;
  const { circle1, circle2 } = data;

  const { x: x1, y: y1, r: r1 } = circle1;
  const { x: x2, y: y2, r: r2 } = circle2;

  const x1Expr = formatShiftedVar("x", x1);
  const y1Expr = formatShiftedVar("y", y1);
  const x2Expr = formatShiftedVar("x", x2);
  const y2Expr = formatShiftedVar("y", y2);

  return (
    <div className="space-y-4">
      <div>Określ wzajemne położenie okręgów:</div>
      <BlockMath math={`${x1Expr} + ${y1Expr} = ${r1 ** 2}`} />
      <BlockMath math={`${x2Expr} + ${y2Expr} = ${r2 ** 2}`} />
    </div>
  );
}

export function renderInput(_, userInput, setUserInput, disabled) {
  return (
    <SelectInput
      options={options}
      value={userInput}
      onChange={(e) => setUserInput(e?.target?.value ?? e)}
      disabled={disabled}
      placeholder="Wybierz"
    />
  );
}

export function checkAnswer(task, userInput) {
  const data = task.data ?? task;
  return userInput === data.position;
}

export function renderAnswer(task) {
  const data = task.data ?? task;
  const { position } = data;

  const label = options.find((o) => o.value === position)?.label;
  return (
    <div style={{ textAlign: "left", marginTop: "1rem" }}>
      Poprawna odpowiedź: <strong>{label}</strong>
    </div>
  );
}

export function renderExplanation(task) {
  const data = task.data ?? task;
  const { circle1, circle2, d, position } = data;
  const { x: x1, y: y1, r: r1 } = circle1;
  const { x: x2, y: y2, r: r2 } = circle2;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const distanceSquared = dx ** 2 + dy ** 2;
  const distanceRoot = Math.sqrt(distanceSquared);
  const exactDistanceLatex = Number.isInteger(distanceRoot)
    ? `\\sqrt{${distanceSquared}} = ${distanceRoot}`
    : `\\sqrt{${distanceSquared}} \\approx ${d.toFixed(2)}`;
  const rSum = r1 + r2;
  const rDiff = Math.abs(r1 - r2);

  // dynamiczny zakres wykresu
  const pad = 2;
  const xmin = Math.min(x1 - r1, x2 - r2) - pad;
  const xmax = Math.max(x1 + r1, x2 + r2) + pad;
  const ymin = Math.min(y1 - r1, y2 - r2) - pad;
  const ymax = Math.max(y1 + r1, y2 + r2) + pad;

  const conditionLatex =
    d === 0 && r1 === r2
      ? "d = 0 \\wedge r_1 = r_2"
      : Math.abs(d - rSum) < 1e-6
      ? "d = r_1 + r_2"
      : Math.abs(d - rDiff) < 1e-6
      ? "d = |r_1 - r_2|"
      : d > rSum
      ? "d > r_1 + r_2"
      : d < rDiff
      ? "d < |r_1 - r_2|"
      : "|r_1 - r_2| < d < r_1 + r_2";

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Odczytujemy dane:
        <BlockMath math={`S_1 = (${x1},\\ ${y1}),\\ r_1 = ${r1}`} />
        <BlockMath math={`S_2 = (${x2},\\ ${y2}),\\ r_2 = ${r2}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Liczymy odległość między środkami:
        <BlockMath
          math={`d = \\sqrt{(${latexDiff(x2, x1)})^2 + (${latexDiff(y2, y1)})^2}`}
        />
        <BlockMath
          math={`= \\sqrt{(${dx})^2 + (${dy})^2} = ${exactDistanceLatex}`}
        />
      </div>

      <div>
        <strong>Krok 3:</strong> Porównujemy z <InlineMath math={"r_1 + r_2"} /> oraz{" "}
        <InlineMath math={"|r_1 - r_2|"} />:
        <BlockMath math={`r_1 + r_2 = ${rSum} \\quad |r_1 - r_2| = ${rDiff}`} />
        <p>
          Ponieważ <InlineMath math={conditionLatex} />, to okręgi{" "}
          <strong>{options.find((o) => o.value === position).label}</strong>.
        </p>
      </div>

      <div style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
        <GraphViewer
          points={{
            S1: { x: x1, y: y1, label: "S₁" },
            S2: { x: x2, y: y2, label: "S₂" },
          }}
          circles={[
            {
              center: { x: x1, y: y1 },
              radius: r1,
              color: "#ff8c1a",
              label: "O₁",
            },
            {
              center: { x: x2, y: y2 },
              radius: r2,
              color: "#9d4edd",
              label: "O₂",
            },
          ]}
          width={500}
          height={500}
          showGrid
          xRange={[xmin, xmax]}
          yRange={[ymin, ymax]}
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput) {
  const label = options.find((o) => o.value === userInput)?.label;
  return label || <i>(brak odpowiedzi)</i>;
}
